import { useState } from "react";
import { Link } from "react-router-dom";
import type { PriceRow, PriceTab } from "@/mocks/priceTables";
import MonitorLpBlock from "@/components/feature/MonitorLpBlock";
import HairRemovalMalePriceBlock from "./HairRemovalMalePriceBlock";
import PriceCategoryCta from "./PriceCategoryCta";
import PriceCountTable from "./PriceCountTable";
import PriceNotice from "./PriceNotice";
import PriceRowTable from "./PriceRowTable";
import { SkinConcernFilterBar, filterBySkinConcern } from "./SkinConcernFilter";

export default function PricePanel({ tab }: { tab: PriceTab }) {
  const [skinConcern, setSkinConcern] = useState<string>("すべて");
  const isSkin = tab.id === "skin";

  // 医療脱毛タブのみ、男性（顔・首）＋男性（ボディ）をチャコール反転のひと続きエリアにする。
  // それ以外のタブは従来どおり全グループを明るい料金表で表示する。
  const isHairRemoval = tab.id === "hair-removal";
  const lightGroups = isHairRemoval
    ? tab.groups.filter((group) => !group.title.startsWith("男性"))
    : tab.groups;
  const darkGroups = isHairRemoval
    ? tab.groups.filter((group) => group.title.startsWith("男性"))
    : [];

  return (
    <div role="tabpanel" aria-label={tab.jp}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="en-eyebrow block text-[10px] text-secondary-700 md:text-[11px]">{tab.en}</span>
          <h2 className="mt-4 font-heading text-[24px] text-foreground-950 md:text-[30px]">{tab.jp}</h2>
        </div>
        <p className="max-w-md text-[13px] leading-[2] text-foreground-700">{tab.lead}</p>
      </div>

      <div className="mt-10 space-y-14 md:mt-12">
        {/* お肌管理は4分類をまたいでお悩みで絞り込む（フィルターは1つ） */}
        {isSkin ? <SkinConcernFilterBar active={skinConcern} onChange={setSkinConcern} /> : null}

        {/* カテゴリの中心施術を「施術名」として明示（任意） */}
        {tab.featured ? (
          <div className="border-y border-foreground-950/15 py-7 md:py-8">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8">
              <div>
                <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">施術名</span>
                <p className="mt-4 font-heading text-[20px] leading-[1.5] text-foreground-950 md:text-[26px]">
                  {tab.featured.name}
                </p>
                <p className="mt-3 text-[12.5px] leading-[1.9] text-foreground-600">{tab.featured.note}</p>
              </div>
              <div className="flex items-baseline gap-3 sm:flex-col sm:items-end sm:gap-2">
                <span className="en-eyebrow text-[10px] text-secondary-500">料金</span>
                <span className="font-label text-[20px] text-foreground-900 md:text-[24px]">
                  {tab.featured.price}
                </span>
              </div>
            </div>
          </div>
        ) : null}

        {lightGroups.map((group) => {
          const rows: PriceRow[] = isSkin ? filterBySkinConcern(group.rows, skinConcern) : group.rows;
          if (isSkin && rows.length === 0) return null;
          return (
            <div key={`${tab.id}-${group.title}`}>
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 md:gap-x-5">
                <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">{group.en}</span>
                <span className="font-heading text-lg text-foreground-950">{group.title}</span>
                <span className="hidden h-px flex-1 bg-foreground-950/10 md:block" />
                {group.href ? (
                  <Link
                    to={group.href}
                    className="group/link inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] text-secondary-700 transition-colors hover:text-secondary-900"
                  >
                    {group.hrefLabel ?? "詳しく見る"}
                    <i
                      className="ri-arrow-right-line text-sm transition-transform group-hover/link:translate-x-0.5"
                      aria-hidden
                    />
                  </Link>
                ) : null}
              </div>

              {group.lead ? (
                <p className="mt-4 text-[12.5px] leading-[1.9] text-foreground-600">{group.lead}</p>
              ) : null}

              {group.columns ? <PriceCountTable group={group} rows={rows} /> : <PriceRowTable rows={rows} />}
            </div>
          );
        })}

        {/* 医療脱毛：男性（顔・首）〜男性（ボディ）をひと続きのチャコール反転エリアに（詳細ページVersion 100と統一） */}
        {darkGroups.length > 0 ? <HairRemovalMalePriceBlock groups={darkGroups} /> : null}

        {/* 新規お試しモニター専用LPへの導線（全料金表示の後に配置。定価を確認した後の案内として独立CTA化） */}
        {tab.monitorLp ? (
          <MonitorLpBlock
            eyebrow={tab.monitorLp.eyebrow}
            title={tab.monitorLp.title}
            description={tab.monitorLp.description}
            label={tab.monitorLp.label}
            url={tab.monitorLp.url}
          />
        ) : null}

        {/* 注記（料金一覧の直下・共通見出し「ご案内・注意事項」） */}
        <PriceNotice items={tab.notice} />

        {/* 該当施術の詳細ページへの導線 */}
        {tab.detailLink ? (
          <Link
            to={tab.detailLink.href}
            className="group/detail inline-flex items-center gap-3 border border-foreground-950/15 px-6 py-4 text-[13.5px] text-foreground-900 transition-colors hover:bg-background-100"
          >
            {tab.detailLink.label}
            <i
              className="ri-arrow-right-line text-base text-secondary-600 transition-transform group-hover/detail:translate-x-1"
              aria-hidden
            />
          </Link>
        ) : null}

        {/* CTA（LINE / 無料カウンセリング）※Medical Slim ZEROは専用LPのため対象外 */}
        {tab.id !== "slim-zero" ? <PriceCategoryCta /> : null}
      </div>
    </div>
  );
}